import { router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { WeeklyScheduleGrid, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { unitsPath } from '@/shared/lib/routes'
import type { WebUnitForm } from '@/shared/types/web'
import {
  ACCREDITATION_FIELDS,
  resolveUnitCreateFollowParentSchedule,
} from '@/entities/organization/units/constants'
import { useFollowParentDepartmentSchedulePreview } from '@/entities/organization/units/useFollowParentDepartmentSchedulePreview'
import {
  hasLicenseOrAccreditationValues,
  isBlankIdentifier,
  normalizeAccreditationForForm,
  snakeCaseAccreditation,
} from '@/entities/organization/units/licenseIdentifiers'

interface DepartmentOption {
  value: number
  label: string
  siteName: string | null
}

interface UnitFormData {
  name: string
  departmentId: number | null
  licenseNumber: string
  followParentSchedule: boolean
  customDaySchedule: WeekdaySchedule
  accreditation: Record<string, string>
}

type UnitFormClientErrors = Partial<
  Record<'name' | 'departmentId' | 'licenseOrAccreditation', string>
>

interface UnitFormProps {
  departments: DepartmentOption[]
  initialData?: Partial<WebUnitForm>
  customDaySchedule?: WeekdaySchedule
  parentDaySchedule?: WeekdaySchedule
  submitUrl: string
  submitMethod: 'post' | 'patch'
}

const buildClientErrors = (
  data: UnitFormData,
  submitMethod: 'post' | 'patch',
): UnitFormClientErrors => {
  const clientErrors: UnitFormClientErrors = {}

  if (isBlankIdentifier(data.name)) {
    clientErrors.name = t('organization.units.form.nameRequired')
  }

  if (submitMethod === 'post' && !data.departmentId) {
    clientErrors.departmentId = t('organization.units.form.departmentRequired')
  }

  if (!hasLicenseOrAccreditationValues(data.licenseNumber, data.accreditation)) {
    clientErrors.licenseOrAccreditation = t(
      'organization.units.form.licenseOrAccreditationRequired',
    )
  }

  return clientErrors
}

const firstErrorFieldId = (clientErrors: UnitFormClientErrors): string => {
  if (clientErrors.name) {
    return 'name'
  }
  if (clientErrors.departmentId) {
    return 'departmentId'
  }
  return 'licenseNumber'
}

const UnitForm = ({
  departments,
  initialData,
  customDaySchedule,
  parentDaySchedule,
  submitUrl,
  submitMethod,
}: UnitFormProps) => {
  const canSubmit = useCan(
    Resources.UNIT,
    submitMethod === 'post' ? Actions.CREATE : Actions.UPDATE,
  )
  const initialDepartmentId = initialData?.departmentId ?? null
  const form = useForm<UnitFormData>({
    name: initialData?.name ?? '',
    departmentId: initialDepartmentId,
    licenseNumber: initialData?.licenseNumber ?? '',
    // Create: legacy ON when omitted. Edit: never invent ON via the create default.
    followParentSchedule:
      submitMethod === 'post'
        ? resolveUnitCreateFollowParentSchedule(initialData?.followParentSchedule)
        : Boolean(initialData?.followParentSchedule),
    customDaySchedule: customDaySchedule ?? { ...ALL_DAYS_ON },
    accreditation: normalizeAccreditationForForm(
      initialData?.accreditation as Record<string, unknown> | null | undefined,
    ),
  })

  // Create only: edit locks department and #edit already resolves the site parent.
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0)
  const { effectiveParentPreview, parentPreviewPending, parentPreviewFailed } =
    useFollowParentDepartmentSchedulePreview({
      enabled: submitMethod === 'post' && form.data.followParentSchedule,
      departmentId: form.data.departmentId,
      parentDaySchedule,
      initialDepartmentId: submitMethod === 'post' ? initialDepartmentId : null,
      retryNonce: previewRetryNonce,
    })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    const clientErrors = buildClientErrors(form.data, submitMethod)
    if (Object.keys(clientErrors).length > 0) {
      form.clearErrors()
      form.setError(clientErrors)
      document
        .getElementById(firstErrorFieldId(clientErrors))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }

    form.transform((data) => ({
      unit: {
        name: data.name.trim(),
        department_id: data.departmentId,
        license_number: data.licenseNumber.trim(),
        follow_parent_schedule: data.followParentSchedule,
        custom_day_schedule: data.customDaySchedule,
        accreditation: snakeCaseAccreditation(data.accreditation),
      },
    }))
    if (submitMethod === 'post') {
      form.post(submitUrl)
    } else {
      form.patch(submitUrl)
    }
  }

  const selectedDepartment = departments.find((dept) => dept.value === form.data.departmentId)
  const clientErrors = form.errors as UnitFormClientErrors

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-xl" noValidate>
      <div className="space-y-1.5">
        <Label htmlFor="name">{t('organization.units.form.name')} *</Label>
        <Input
          id="name"
          value={form.data.name}
          onChange={(event) => form.setData('name', event.target.value)}
          placeholder={t('organization.units.form.namePlaceholder')}
          required
        />
        {form.errors.name && <p className="text-sm text-destructive">{form.errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="departmentId">
          {t('organization.units.form.department')}
          {submitMethod === 'post' ? ' *' : ''}
        </Label>
        <SearchableSelect
          id="departmentId"
          value={form.data.departmentId?.toString() ?? ''}
          onValueChange={(value) => form.setData('departmentId', value ? Number(value) : null)}
          disabled={submitMethod === 'patch'}
          placeholder={t('organization.units.form.selectDepartment')}
          aria-required={submitMethod === 'post'}
          options={departments.map((dept) => ({
            value: dept.value.toString(),
            label: dept.siteName ? `${dept.label} (${dept.siteName})` : dept.label,
          }))}
        />
        {form.errors.departmentId && (
          <p className="text-sm text-destructive">{form.errors.departmentId}</p>
        )}
        {selectedDepartment?.siteName && (
          <p className="text-sm text-muted-foreground">{selectedDepartment.siteName}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="licenseNumber">{t('organization.units.form.licenseNumber')}</Label>
        <Input
          id="licenseNumber"
          value={form.data.licenseNumber}
          onChange={(event) => form.setData('licenseNumber', event.target.value)}
          placeholder={t('organization.units.form.licenseNumberPlaceholder')}
        />
        {form.errors.licenseNumber && (
          <p className="text-sm text-destructive">{form.errors.licenseNumber}</p>
        )}
      </div>

      <div
        className={cn(
          'space-y-3 rounded-lg border p-5',
          clientErrors.licenseOrAccreditation && 'border-destructive',
        )}
      >
        <p className="text-sm font-medium">{t('organization.units.form.accreditationSection')}</p>
        <div className="grid grid-cols-2 gap-3">
          {ACCREDITATION_FIELDS.map(({ key, label }) => (
            <div key={key} className="space-y-1.5">
              <Label htmlFor={`accreditation_${key}`}>{label}</Label>
              <Input
                id={`accreditation_${key}`}
                value={form.data.accreditation[key] ?? ''}
                onChange={(event) =>
                  form.setData('accreditation', {
                    ...form.data.accreditation,
                    [key]: event.target.value,
                  })
                }
              />
            </div>
          ))}
        </div>
        {clientErrors.licenseOrAccreditation && (
          <p className="text-sm text-destructive">{clientErrors.licenseOrAccreditation}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>{t('schedule.loggingSchedule')}</Label>
        <WeeklyScheduleGrid
          value={form.data.customDaySchedule}
          onChange={(next) => form.setData('customDaySchedule', next)}
          followParent={form.data.followParentSchedule}
          onFollowParentChange={(checked) => form.setData('followParentSchedule', checked)}
          parentPreview={effectiveParentPreview}
          parentPreviewPending={parentPreviewPending}
          parentPreviewFailed={parentPreviewFailed}
          onParentPreviewRetry={() => setPreviewRetryNonce((nonce) => nonce + 1)}
          parentLabel={t('schedule.followSiteSchedule')}
        />
        {form.errors.customDaySchedule && (
          <p className="text-sm text-destructive">{form.errors.customDaySchedule}</p>
        )}
      </div>

      {!canSubmit && <NoAccessNotice />}

      <div className="flex gap-3 pt-2">
        {canSubmit && (
          <Button type="submit" disabled={form.processing}>
            {form.processing
              ? t('organization.units.form.submitting')
              : t('organization.units.form.submit')}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => router.visit(unitsPath())}>
          {t('organization.units.form.cancel')}
        </Button>
      </div>
    </form>
  )
}

export default UnitForm
